import type { ClassificationResult } from '@classification-testing/domain';
import { ContentType } from '@classification-testing/domain';
import { formatTimestamp, getContentDisplayText } from '@core/utils';
import { IconButton, Eye, IconButtonSize, IconSize, StatusCircleBlock } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './ClassificationResultDialog.css';

/**
 * Helper function to get content display information
 */
export function getContentDisplay(result: ClassificationResult) {
  const content = getContentDisplayText(result.text, result.contentUrl);

  if (result.contentType === ContentType.AUDIO && result.text) {
    return { label: 'Transcript', content };
  }

  if (result.contentType === ContentType.TEXT && result.text) {
    return { label: 'Text', content };
  }

  if (result.contentType === ContentType.USERNAME && result.text) {
    return { label: 'Username', content };
  }

  if (result.contentType === ContentType.IMAGE) {
    return { label: 'Content Text', content: result.text };
  }

  return null;
}

/**
 * Audio Metadata section component
 */
function AudioMetadataSection({ result }: { result: ClassificationResult }) {
  if (result.contentType !== ContentType.AUDIO || !result.duration) {
    return null;
  }

  return (
    <div className={styles.audioMetadata}>
      <div className={styles.metadataGrid}>
        <div className={styles.metadataItemInline}>
          <span className={styles.metadataLabel}>Audio Duration:</span>
          <span className={styles.metadataValue}>{(result.duration / 1000).toFixed(2)}s</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Helper component for image error display
 */
function ImageErrorDisplay({ contentUrl }: { contentUrl: string }) {
  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Image Content</h3>
      <div className={styles.imageErrorContainer}>
        <p className={styles.imageErrorText}>
          Unable to display image preview. Ensure URL is valid. CORS restrictions may prevent preview but classification was completed.
        </p>
        <p className={styles.imageUrlText}>{contentUrl}</p>
      </div>
    </div>
  );
}

/**
 * Helper component for interactive image container
 */
function InteractiveImageContainer({
  canToggleBlur,
  isContentBlurred,
  onToggleBlur,
  children,
}: {
  canToggleBlur: boolean;
  isContentBlurred: boolean;
  onToggleBlur: () => void;
  children: React.ReactNode;
}) {
  const handleClick = () => {
    if (canToggleBlur) {
      onToggleBlur();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (canToggleBlur && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggleBlur();
    }
  };

  const title = canToggleBlur ? (isContentBlurred ? 'Click to show content' : 'Click to hide content') : undefined;

  if (canToggleBlur) {
    return (
      <div
        aria-label={isContentBlurred ? 'Show content' : 'Hide content'}
        className={styles.imageContainer}
        role="button"
        style={{ cursor: 'pointer' }}
        tabIndex={0}
        title={title}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    );
  }

  return <div className={styles.imageContainer}>{children}</div>;
}

/**
 * Image Display section component
 */
export function ImageDisplaySection({
  result,
  isContentBlurred,
  onToggleBlur,
  isSafe,
}: {
  result: ClassificationResult;
  isContentBlurred: boolean;
  onToggleBlur?: () => void;
  isSafe: boolean;
}) {
  const [imageError, setImageError] = useState(false);

  if (result.contentType !== ContentType.IMAGE || !result.contentUrl) {
    return null;
  }

  if (imageError) {
    return <ImageErrorDisplay contentUrl={result.contentUrl} />;
  }

  // Only allow blur toggle for unsafe content
  const canToggleBlur = !isSafe && Boolean(onToggleBlur);

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Image Content</h3>
      <InteractiveImageContainer canToggleBlur={canToggleBlur} isContentBlurred={isContentBlurred} onToggleBlur={onToggleBlur ?? (() => {})}>
        <img
          alt={`Classified content from ${formatTimestamp(result.timestamp)}`}
          className={`${styles.fullImage} ${isContentBlurred ? styles.blurredImage : ''}`}
          src={result.contentUrl}
          onError={() => setImageError(true)}
        />
      </InteractiveImageContainer>
    </div>
  );
}

/**
 * Content Display section component
 */
export function ContentDisplaySection({
  result,
  isContentBlurred,
  onToggleBlur,
  isSafe,
}: {
  result: ClassificationResult;
  isContentBlurred: boolean;
  onToggleBlur?: () => void;
  isSafe: boolean;
}) {
  const contentDisplay = getContentDisplay(result);

  if (!contentDisplay) {
    return null;
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>{contentDisplay.label}</h3>
      <div className={styles.contentBoxContainer}>
        <div className={styles.contentBox}>
          <span className={isContentBlurred ? styles.blurredText : ''}>{contentDisplay.content}</span>
        </div>
        {!isSafe && onToggleBlur && (
          <div className={styles.visibleButton}>
            <IconButton
              aria-label={isContentBlurred ? 'Show content' : 'Hide content'}
              buttonSize={IconButtonSize.Small}
              icon={isContentBlurred ? Eye : StatusCircleBlock}
              iconSize={IconSize.Small}
              title={isContentBlurred ? 'Show content' : 'Hide content'}
              onClick={onToggleBlur}
            />
          </div>
        )}
      </div>
      <AudioMetadataSection result={result} />
    </div>
  );
}
